import { currentTheme } from '@/themes/index';
import { i18n } from '@/i18n';
import { mdEditorUrl } from '@/utils/tools';
import { VITE_INTERPAGE_HOST, VITE_COOKIE_DOMAIN } from '@/utils/tools';
const cookieDomain = VITE_COOKIE_DOMAIN();
interface WindowWithOpen extends Window {
  originalOpen?: typeof window.open;
}

/**
 * 处理中间页链接
 * url 目标跳转链接
 */
function getInterLink(url: string) {
  const { locale } = i18n.global;
  const interLink = new URL(VITE_INTERPAGE_HOST());
  interLink.searchParams.append('target', url);
  interLink.searchParams.append('from', location.href);
  interLink.searchParams.append('lang', locale.value);
  interLink.searchParams.append('theme', currentTheme.value);
  return interLink;
}

/**
 * 默认白名单配置
 */
const DEFAULT_ALLOW_DOMAINS = [
  'gitcode.com',
  'gitcode.net',
  'csdn.net', // csdn全站
  'huaweicloud.com', // 华为云全站
  'docs.github.com', // github文档
  'docs.gitlab.com' // gitlab文档
];

const DEFAULT_ALLOW_URLS = [
  'https://beian.miit.gov.cn/#/Integrated/index', // icp备案
  'https://beian.mps.gov.cn/#/query/webSearch?code=11010502055713', // 公安备案
  'https://www.zhipin.com/gongsi/job/b1eccc6dba1cc0d41Hd60tW0GVc~.html?ka=company-jobs', // 招贤纳士
  'https://gitee.com/profile/personal_access_tokens' // gitee获取AK
];
const OSS_COMPASS_URLS = [
  'https://oss-compass.org/zh/docs/service-guide', // OSS Compass 服务指南
  'https://oss-compass.org/docs/service-guide' // OSS Compass 服务指南
];

interface WhiteListConfig {
  allowDomains: string[];
  allowUrls: string[];
}
/**
 * 获取白名单配置（若配置获取失败，则使用本地默认配置）
 */
async function getWhiteListConfig(): Promise<WhiteListConfig> {
  const mainDomain = cookieDomain;
  try {
    const configUrl = `https://cdn-static.gitcode.com/linkPage/linkList.json?timestamp=${new Date().getTime()}`;
    const response = await fetch(configUrl);
    if (!response.ok) throw new Error('Failed to fetch whitelist config');
    const config = await response.json();
    return {
      allowDomains: [...(config.allowDomains || DEFAULT_ALLOW_DOMAINS), mainDomain], // 需要把当前运行的域名加上
      allowUrls: [...OSS_COMPASS_URLS, ...(config.allowUrls || DEFAULT_ALLOW_URLS)]
    };
  } catch (error) {
    console.warn('Failed to load whitelist config, using default:', error);
    return {
      allowDomains: DEFAULT_ALLOW_DOMAINS,
      allowUrls: [...OSS_COMPASS_URLS, ...DEFAULT_ALLOW_URLS]
    };
  }
}

/**
 * 拦截第三方链接跳转
 */
export const handleExternalLink = async () => {
  const { allowDomains, allowUrls } = await getWhiteListConfig();

  // 判断是否为需要跳转中间页的第三方链接
  const isExternalLink = (url: string, target?: HTMLElement): boolean => {
    // ai聊天站点不需要拦截
    const isAiChat = target && document.querySelector('.open-link-ignore')?.contains(target);
    if (isAiChat) return false;

    // 如果是相对路径，则直接跳转
    if (!/^https?:\/\//.test(url)) return false;

    const urlObj = new URL(url);
    // 检查是否匹配允许的域名
    if (allowDomains.some(domain => urlObj.hostname.endsWith(domain))) {
      return false;
    }
    // 检查是否完全匹配允许的链接（兼容最后带斜杠）
    if (allowUrls.includes(url.replace(/\/$/, ''))) {
      return false;
    }

    return true;
  };

  // 拦截1：处理a标签点击事件委托
  // 使用捕获阶段（useCapture: true）确保在子元素事件处理器之前执行(比如 MdRender 的 handleAnchorClickEventFunc)
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLAnchorElement;
    const anchorElement = target.closest('a');
    if (!anchorElement?.href) return;
    // 相对链接不处理
    if (/^https?:\/\//.test(anchorElement.href)) {
      anchorElement.href = mdEditorUrl(anchorElement.href, true);
    }
    if (isExternalLink(anchorElement.href, target)) {
      event.preventDefault();
      // 绝对链接域名同质化处理
      anchorElement.href = mdEditorUrl(anchorElement.href, true);
      window.open(getInterLink(anchorElement.href));
    }
  }, true); // 使用捕获阶段，确保先于子元素事件处理器执行

  // 拦截2：重写window.open
  const customWindow = window as WindowWithOpen;
  customWindow.originalOpen = customWindow.originalOpen || window.open;

  window.open = function (url?: string | URL, target?: string, features?: string): Window | null {
    if (!url) return null;
    const urlString = url.toString();
    const targetUrl = isExternalLink(urlString) ? getInterLink(urlString) : urlString;
    return customWindow.originalOpen?.call(window, targetUrl, target, features) || null;
  };
};
